// Widget layout registry — shared by the admin layout picker and the widget
// renderer so the two never drift. A widget's chosen layout lives in
// widget_configs.settings.layout (free-form jsonb, no migration).
//
// `available: false` layouts are designed but not yet implemented in the
// widget: the picker shows them as "coming soon" (not selectable) and the
// widget falls back to "classic" if one is somehow stored. As each layout
// ships, flip its `available` to true.

export type WidgetLayoutId =
  | "classic"
  | "swipe-stack"
  | "hero-overlay"
  | "estate-console"
  | "smart-cart"
  | "wizard"
  | "catalog-drawer"
  | "pinned-bar"
  | "accordion";

export interface WidgetLayoutOption {
  id: WidgetLayoutId;
  label: string;
  description: string;
  available: boolean;
}

export const DEFAULT_WIDGET_LAYOUT: WidgetLayoutId = "classic";

export const WIDGET_LAYOUTS: WidgetLayoutOption[] = [
  {
    id: "classic",
    label: "Classic",
    description:
      "The standard layout — service booking with the cross-sell sidebar. The default.",
    available: true,
  },
  {
    id: "swipe-stack",
    label: "Swipe Stack",
    description: "Mobile-first horizontal scroller; cross-sell as a swipe row.",
    available: true,
  },
  {
    id: "hero-overlay",
    label: "Hero Overlay",
    description:
      "Editorial hero band with the booking overlaid; cross-sell rail alongside.",
    available: true,
  },
  {
    id: "estate-console",
    label: "Estate Console",
    description: "Dense dashboard — booking, cross-sell, and events above the fold.",
    available: true,
  },
  {
    id: "smart-cart",
    label: "Smart Cart Rail",
    description: "A cart-forward rail that doubles as the cross-sell engine.",
    available: true,
  },
  {
    id: "wizard",
    label: "Stepped Wizard",
    description: "One decision per step; cross-sell is the add-ons step.",
    available: true,
  },
  {
    id: "catalog-drawer",
    label: "Catalog + Drawer",
    description: "Service grid with a slide-in booking drawer.",
    available: true,
  },
  {
    id: "pinned-bar",
    label: "Pinned Booking Bar",
    description: "Content scrolls; a cart bar stays pinned to the bottom.",
    available: true,
  },
  {
    id: "accordion",
    label: "Accordion",
    description: "Services as a vertical accordion; expand one to book.",
    available: true,
  },
];

const BY_ID = new Map<string, WidgetLayoutOption>(
  WIDGET_LAYOUTS.map((l) => [l.id, l]),
);

/**
 * Resolve a stored layout value to one the widget can actually render.
 * Unknown or not-yet-available layouts fall back to the classic layout, so
 * a tenant never ends up with a broken widget.
 */
export function resolveWidgetLayout(value: unknown): WidgetLayoutId {
  if (typeof value !== "string") return DEFAULT_WIDGET_LAYOUT;
  const opt = BY_ID.get(value);
  return opt && opt.available ? opt.id : DEFAULT_WIDGET_LAYOUT;
}

export type WidgetBreakpoint = "desktop" | "mobile";

export interface WidgetLayoutSelection {
  desktop: WidgetLayoutId;
  mobile: WidgetLayoutId;
}

/**
 * Viewport width (px) below which the widget uses the `mobile` layout.
 * Above it, the `desktop` layout.
 */
export const WIDGET_LAYOUT_MOBILE_BREAKPOINT = 720;

/**
 * Resolve the stored layout setting into a per-breakpoint selection.
 * Accepts either the new shape `{ desktop, mobile }` or a bare string
 * (legacy / single-layout) which applies to both breakpoints. Every value
 * is run through resolveWidgetLayout, so unavailable layouts fall back to
 * classic per breakpoint.
 */
export function resolveWidgetLayouts(value: unknown): WidgetLayoutSelection {
  if (typeof value === "string") {
    const l = resolveWidgetLayout(value);
    return { desktop: l, mobile: l };
  }
  if (value && typeof value === "object") {
    const v = value as Record<string, unknown>;
    return {
      desktop: resolveWidgetLayout(v.desktop),
      mobile: resolveWidgetLayout(v.mobile),
    };
  }
  return { desktop: DEFAULT_WIDGET_LAYOUT, mobile: DEFAULT_WIDGET_LAYOUT };
}
